import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GeocodeCandidate {
  label: string;
  lat: number;
  lng: number;
  street?: string;
  city?: string;
  county?: string;
  postcode?: string;
  country?: string;
}

interface GeocodeResponse {
  items: GeocodeCandidate[];
  error?: string;
}

interface UseAddressSearchOptions {
  debounceMs?: number;
  minLength?: number;
}

export function useAddressSearch(options: UseAddressSearchOptions = {}) {
  const { debounceMs = 400, minLength = 4 } = options;
  
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<GeocodeCandidate[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>('');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const search = useCallback(async (params: {
    address: string;
    city?: string;
    region?: string;
  }) => {
    const query = params.address?.trim() || '';
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // Clear results if query is too short
    if (query.length < minLength) {
      setResults([]);
      setError(null);
      setIsSearching(false);
      return;
    }

    // Skip if same query
    if (query === lastQueryRef.current) {
      return;
    }

    setIsSearching(true);
    setError(null);

    debounceRef.current = setTimeout(async () => {
      lastQueryRef.current = query;

      try {
        const response = await supabase.functions.invoke<GeocodeResponse>('geocode-address', {
          body: {
            address: query,
            city: params.city || undefined,
            region: params.region || undefined,
            country: 'Sweden',
            limit: 8,
          },
        });

        if (response.error) {
          throw new Error(response.error.message || 'Geocoding failed');
        }

        const data = response.data;

        if (data?.error) {
          throw new Error(data.error);
        }

        setResults(data?.items || []);
        setError(null);
      } catch (err) {
        console.error('Address search error:', err);
        setError('Kunde inte hämta adressförslag');
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, debounceMs);
  }, [debounceMs, minLength]);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    lastQueryRef.current = '';
  }, []);

  return {
    search,
    clearResults,
    isSearching,
    results,
    error,
  };
}
